import React, { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../services/msalConfig";
import toast from "react-hot-toast";
 

const ScheduleCheckModal = ({ isOpen, onClose, boardEmails = [], interviewDate }) => {
  const { instance, accounts } = useMsal();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const checkAvailability = async () => {
    if (!interviewDate || boardEmails.length === 0) {
      toast.error("Please provide a valid date and board members.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const account = accounts[0];
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account
      });

      const accessToken = response.accessToken;

      const startTime = `${interviewDate}T09:00:00`;
      const endTime = `${interviewDate}T17:00:00`;

      const res = await fetch("https://graph.microsoft.com/v1.0/me/calendar/getSchedule", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          schedules: boardEmails,
          startTime: {
            dateTime: startTime,
            timeZone: "UTC"
          },
          endTime: {
            dateTime: endTime,
            timeZone: "UTC"
          },
          availabilityViewInterval: 30
        })
      });

      const data = await res.json();
      const combinedAvailability = new Array(16).fill("0"); // 8 hours @ 30-min slots = 16 slots

      for (let person of data.value) {
        if (!person.availabilityView) continue;
        for (let i = 0; i < person.availabilityView.length; i++) {
          if (person.availabilityView[i] !== "0") {
            combinedAvailability[i] = "1"; // mark as busy
          }
        }
      }

      const freeSlots = [];
      const base = new Date(`${interviewDate}T09:00:00Z`);

      combinedAvailability.forEach((slot, index) => {
        if (slot === "0") {
          const start = new Date(base.getTime() + index * 30 * 60000);
          const end = new Date(start.getTime() + 30 * 60000);
          freeSlots.push(`${start.toISOString().substring(11, 16)} - ${end.toISOString().substring(11, 16)}`);
        }
      });

      setSlots(freeSlots);
    } catch (err) {
      console.error(err);
      toast.error("Failed to check availability.");
    }

    setLoading(false);
  };

  return (
 <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.2)' }}>
  <div 
    className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-full sm:max-w-md md:max-w-lg lg:max-w-xl"
    style={{ 
      borderColor: 'rgba(18, 76, 114, 0.1)', 
      borderWidth: '1px',
    }}
  >
    {/* Modal Header */}
    <div className="flex items-center justify-between p-6 border-b rounded-t-2xl" style={{ 
      borderColor: 'rgba(18, 76, 114, 0.1)',
      background: 'linear-gradient(to right, rgba(18, 76, 114, 0.05), transparent)'
    }}>
      <h2 className="text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>
        Interview Board Availability
      </h2>
      <button 
        onClick={onClose}
        className="transition-all rounded-full p-1.5"
        style={{ color: 'rgba(18, 76, 114, 0.6)' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgb(18, 76, 114)';
          e.currentTarget.style.color = 'white';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = 'rgba(18, 76, 114, 0.6)';
        }}
        aria-label="Close"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    {/* Modal Body */}
    <div className="p-6 space-y-6">
      {/* Date and Members Info */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 rounded-lg" 
        style={{ backgroundColor: 'rgba(18, 76, 114, 0.03)', border: '1px solid rgba(18, 76, 114, 0.1)' }}>
        
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4" style={{ color: 'rgb(18, 76, 114)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className="text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Board Members</p>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4" style={{ color: 'rgb(18, 76, 114)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Date: <span className="font-normal">{interviewDate}</span></p>
          </div>
        </div>
      </div>

      {/* Board Members List */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'rgb(18, 76, 114)' }}></div>
          <p className="text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Selected Members:</p>
        </div>
        
        <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
          {boardEmails.map((email, idx) => (
            <div 
              key={idx}
              className="flex items-center gap-3 p-3 rounded-lg transition-all"
              style={{ 
                backgroundColor: idx % 2 === 0 ? 'rgba(18, 76, 114, 0.02)' : 'transparent',
                border: '1px solid rgba(18, 76, 114, 0.1)'
              }}
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium" 
                style={{ backgroundColor: 'rgba(18, 76, 114, 0.8)' }}>
                {email.charAt(0).toUpperCase()}
              </div>
              <p className="text-sm text-gray-700 truncate">{email}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Check Availability Button */}
      <div className="flex justify-center">
        <button
          onClick={checkAvailability}
          disabled={loading}
          className="px-6 py-3 text-white font-medium rounded-lg transition-all relative group"
          style={{ 
            backgroundColor: loading ? 'rgba(18, 76, 114, 0.5)' : 'rgb(18, 76, 114)',
            cursor: loading ? 'not-allowed' : 'pointer',
            boxShadow: loading ? 'none' : '0 10px 15px -3px rgba(18, 76, 114, 0.2)',
            minWidth: '180px'
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.opacity = '0.9';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.currentTarget.style.opacity = '1';
            }
          }}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Checking...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Check Availability
            </span>
          )}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-start gap-3 p-3 rounded-lg" 
          style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#ef4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm" style={{ color: '#ef4444' }}>{error}</p>
        </div>
      )}

      {/* Available Slots */}
      {slots.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#10b981' }}></div>
            <h3 className="text-base font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Available Time Slots</h3>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-2">
            {slots.map((slot, idx) => (
              <div 
                key={idx}
                className="p-3 rounded-lg transition-all cursor-pointer group"
                style={{ 
                  backgroundColor: 'rgba(18, 76, 114, 0.05)',
                  border: '1px solid rgba(18, 76, 114, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.1)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.05)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>{slot}</span>
                  <svg className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'rgba(18, 76, 114, 0.6)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Slots Found */}
      {slots.length === 0 && !loading && !error && (
        <div className="flex flex-col items-center justify-center p-6 rounded-lg text-center" 
          style={{ backgroundColor: 'rgba(18, 76, 114, 0.03)', border: '1px dashed rgba(18, 76, 114, 0.2)' }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" 
            style={{ backgroundColor: 'rgba(18, 76, 114, 0.1)' }}>
            <svg className="w-6 h-6" style={{ color: 'rgba(18, 76, 114, 0.6)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-sm font-medium mb-1" style={{ color: 'rgb(18, 76, 114)' }}>No Common Free Slots Found</p>
          <p className="text-xs" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>
            The selected board members don't have common availability on this date.
          </p>
        </div>
      )}
    </div>
  </div>
</div>
  );
};

export default ScheduleCheckModal;
